/**
 * 仿拍镜头结构轨的数据派生(BPWB-1,2026-09-03)。
 *
 * ★与 ai 仓 `app/modules/video/blueprint/scaffold.py` 的 `_plan_takes` / `_take_duration`
 *   **同源镜像**:常量(24 卡上限 / 3~12s 钳 / 4s 兜底 / 24 拍总上限)、timeRange 正则、
 *   长镜等分切拍、超上限从最长卡退回钳断 —— 逐条对着那边写。改那边必须跟这边,
 *   否则创建页画的结构轨和真出的分镜会漂(报价/秒数同型雷,那边头注有账)。
 * ★这里只做**展示**(块宽/秒数/「自动切 N 拍」徽标),全部标「约」:渲染层还有
 *   参考段 2.0s 地板等钳制,精确秒数以编排回包 panelDurations 为准。
 */
import type { SceneCard } from "./types";

const MAX_CARDS = 24;
const DUR_MIN = 3;
const DUR_MAX = 12;
const DUR_FALLBACK = 4;
const MAX_TAKES = MAX_CARDS;

/** 与 ai 侧 `_TIME_RANGE_RE` 同式:`0-2.5s` / `6~13.5` / `19—24秒` 都认。 */
const TIME_RANGE_RE = /(\d+(?:\.\d+)?)\s*[-~—至]+\s*(\d+(?:\.\d+)?)/;

export interface ShotRailItem {
  /** 原片区间(秒);无可用 timeRange 时为 null(那一镜按兜底档铺,画面回文字想象)。 */
  start: number | null;
  end: number | null;
  /** 这张卡最终会铺成几拍(>1 = 长镜自动切拍)。 */
  takeCount: number;
  /** 这张卡对应的成片秒数(各拍钳制后之和,整数)。 */
  durSec: number;
  /** 一句话场景描述(beat > action > visual > subject 摘要)。 */
  desc: string;
}

export interface ShotRailPlan {
  items: ShotRailItem[];
  /** 成片总秒数估计(Σ 各拍钳制时长,与 ai `estimate_copy_duration` 第一项同口径)。 */
  totalSec: number;
  /** 总拍数(= 成片镜数)。 */
  takeTotal: number;
}

function spanOf(card: SceneCard): [number, number] | null {
  const m = TIME_RANGE_RE.exec(card.timeRange ?? "");
  if (!m) return null;
  const a = Number(m[1]);
  const b = Number(m[2]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  return [a, b];
}

/** [a,b] 等分切拍(每拍 ≤DUR_MAX);末拍 end 用 b 原值(同 ai:那个值是剪段 key)。 */
function splitSpan(a: number, b: number): [number, number][] {
  const span = b - a;
  if (span <= DUR_MAX) return [[a, b]];
  const k = Math.ceil(span / DUR_MAX);
  const w = span / k;
  return Array.from({ length: k }, (_v, i) => [a + i * w, i === k - 1 ? b : a + (i + 1) * w]);
}

function takeDuration(span: [number, number] | null): number {
  if (!span) return DUR_FALLBACK;
  // ★与 ai 的已知微差:Python round() 是银行家舍入(x.5 取偶),JS Math.round 恒进位 ——
  //   恰逢 .5 的拍会差 1 秒。展示层全部写「约」,精确值以编排回包 panelDurations 为准。
  return Math.max(DUR_MIN, Math.min(DUR_MAX, Math.round(span[1] - span[0])));
}

function descOf(card: SceneCard): string {
  const pick = [card.beat, card.action, card.visual, card.subject].find(
    (v) => typeof v === "string" && v.trim()
  );
  return (pick ?? "").trim();
}

/** 场景卡 → 结构轨。卡序不动;总拍数超上限时从最长卡退回钳断(同 ai `_plan_takes`)。 */
export function planShotRail(cards: SceneCard[] | null | undefined): ShotRailPlan {
  const clean = (Array.isArray(cards) ? cards : [])
    .filter((c): c is SceneCard => !!c && typeof c === "object")
    .slice(0, MAX_CARDS);
  const spans = clean.map(spanOf);
  const plans: ([number, number] | null)[][] = spans.map((s) =>
    s ? splitSpan(s[0], s[1]) : [null]
  );
  let total = plans.reduce((n, p) => n + p.length, 0);
  if (total > MAX_TAKES) {
    const longest = plans
      .map((p, i) => ({ i, len: p.length, span: spans[i] }))
      .filter((x) => x.len > 1 && x.span)
      .sort((x, y) => y.span![1] - y.span![0] - (x.span![1] - x.span![0]));
    for (const x of longest) {
      if (total <= MAX_TAKES) break;
      total -= plans[x.i].length - 1;
      plans[x.i] = [spans[x.i]];
    }
  }
  const items: ShotRailItem[] = clean.map((card, i) => {
    const takes = plans[i];
    const span = spans[i];
    return {
      start: span ? span[0] : null,
      end: span ? span[1] : null,
      takeCount: takes.length,
      durSec: takes.reduce((n, sp) => n + takeDuration(sp), 0),
      desc: descOf(card),
    };
  });
  return {
    items,
    totalSec: items.reduce((n, it) => n + it.durSec, 0),
    takeTotal: plans.reduce((n, p) => n + p.length, 0),
  };
}

/** `0–2.5s` 风格的时段标签;无时间轴给空串(渲染层自己决定占位)。 */
export function railTimeLabel(item: ShotRailItem): string {
  if (item.start == null || item.end == null) return "";
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  return `${fmt(item.start)}–${fmt(item.end)}s`;
}
